import { Alert, Button } from "@svea/design-system-components-react";
import { Accordion, Heading, Markdown, Stack, useText } from "@sveabank/cc-common";
import { useState } from "react";
import Content from "../../components/content";
import PageNavigationFooter from "../../components/page-navigation-footer";
import Paper from "../../components/paper";
import ReviewSection from "../../components/review-section";
import { withLoader } from "../../hoc/with-loader";
import { useApplicationDetails } from "../../hooks/application";
import { usePageContext } from "../../hooks/context";
import { useSteps } from "../../hooks/useSteps";
import { stepIs } from "../../utils/application";
import CustomerAgreementSection from "./customer-agreement-section";
import FinalizeModalMissingBo from "./finalize-modals/finalize-missing-bo-modal";
import FinalizeModal from "./finalize-modals/finalize-modal";
import GuarantorSection from "./guarantor-section";
import { useSignInformation } from "./hooks/hooks";

const SignPage = () => {
	const { isSigningComplete } = usePageContext(useSignInformation);
	const { isCurrentUserApplicant, applicant } = useApplicationDetails();
	const { isOrgEf, hasCreditProduct, someStepExists, finalizeApplication } =
		usePageContext(useSignInformation);
	const { steps } = useSteps();
	const [finalizeModalOpen, setFinalizeModalOpen] = useState(false);
	const [finalizeMissingBoOpen, setFinalizeMissingBo] = useState(false);
	const hasKycWarning =
		steps?.find(stepIs("ConfirmBeneficialOwnershipStep"))?.state.status === "Declined";

	const handleFinalizeClick = async () => {
		await finalizeApplication();
		if (hasKycWarning) {
			setFinalizeMissingBo(true);
		} else {
			setFinalizeModalOpen(true);
		}
	};
	const [t, texts] = useText();

	const isOngoing = steps?.find(stepIs("FinalizeStep"))?.state.applicationStatus === "Ongoing";
	const applicantFullName = `${applicant?.firstName} ${applicant?.lastName}`;

	return (
		<Content size={12} my={8}>
			<Stack direction="col" gap={8}>
				{!isSigningComplete && (
					<Alert kind="warning">
						{isCurrentUserApplicant
							? t(texts.summaryPage.waitForSignNotice)
							: t(texts.summaryPage.externalSignerInformation, applicantFullName)}
					</Alert>
				)}
				{hasKycWarning && (
					<Alert kind="error">
						<Markdown>{t(texts.summaryPage.kycBoInvalid)}</Markdown>
					</Alert>
				)}
				<section>
					<Paper>
						<Accordion expandedDefault>
							<Accordion.Heading>{t(texts.signPage.reviewPage.title)}</Accordion.Heading>
							<Accordion.Content>
								<ReviewSection />
							</Accordion.Content>
						</Accordion>
					</Paper>
				</section>

				{hasCreditProduct && !isOrgEf ? (
					<>
						<Heading.H3>{t(texts.signPage.package.packageSigningTitle)}</Heading.H3>
						<CustomerAgreementSection sendEmailButton={false} />
						<Heading.H3>{t(texts.signPage.package.guarantorSigningTitle)}</Heading.H3>
						<GuarantorSection altText={t(texts.summaryPage.guarantorSignText)} />
					</>
				) : (
					<CustomerAgreementSection />
				)}
			</Stack>
			{someStepExists("FinalizeStep") && isCurrentUserApplicant && isOngoing && (
				<PageNavigationFooter>
					<Button
						type="button"
						onPress={handleFinalizeClick}
						data-tracking-action="Finalize Application"
					>
						{t(texts.common.finalizeApplication)}
					</Button>
				</PageNavigationFooter>
			)}
			<FinalizeModal isOpen={finalizeModalOpen} setOpen={setFinalizeModalOpen} />
			<FinalizeModalMissingBo isOpen={finalizeMissingBoOpen} setOpen={setFinalizeMissingBo} />
		</Content>
	);
};

export default withLoader(useSignInformation, SignPage);
